import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import ImgThemed from '@/components/ImgThemed/ImgThemed';
import { TopMenuItems } from '../TopMenuItems/TopMenuItems';
import logo from '@/assets/images/logo-main.svg';
import logoDark from '@/assets/images/logo-main-dark.svg';
import { RightMenuItems } from '@/components/RightMenuItems/RightMenuItems';
import SignInButton from '../SignInButton/SignInButton';
import { useAuth0 } from '@auth0/auth0-react';
import ViewAsClientBlock from '@/components/ViewAsClientBlock/ViewAsClientBlock';
import { useIsUpToTablet } from '@/hooks/useMediaQuery';

const Wrapper = styled.div<{ $isRemittance?: boolean }>`
    position: fixed;
    top: 0;
    height: 50px;
    width: 100%;
    padding: 10px 0;
    margin-bottom: 32px;
    background: ${(props) => props.theme['Surface/page']};
    /* The responsive remittance layout pairs the header with the lavender expiry pill just
       beneath it, so its border picks up the same accent. Desktop keeps the original
       neutral divider, since the pill only exists from tablet down. */
    border-bottom: 1px solid
        ${(props) =>
            props.$isRemittance
                ? props.theme['Categorical/5/emphasis']
                : props.theme['Borders/quiet']};
    z-index: 999;
`;
const Content = styled.div<{ $centered?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: ${(props) => (props.$centered ? 'center' : 'space-between')};
    padding: 0 40px;
    margin: 0 auto;
`;
const Logo = styled.div<{ $centered?: boolean }>`
    margin-top: 8px;
    /* The gap only separates the logo from the nav items; with the logo centred and
       alone in the bar it would push it off centre. */
    margin-right: ${(props) => (props.$centered ? 0 : '40px')};
    img {
        width: 40px;
    }
`;

const LeftSideContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const MainNavigation = () => {
    const { isAuthenticated } = useAuth0();
    const { pathname } = useLocation();
    const isUpToTablet = useIsUpToTablet();
    // Remittance is a public page with no nav items either side, so it never shows the
    // sign-in button — at any width.
    const isRemittance = pathname.includes('remittance');
    // Centring the logo and tinting the border belong to the responsive rework only;
    // desktop kept its original header.
    const isRemittanceResponsive = isRemittance && isUpToTablet;
    return (
        <Wrapper id="main-navigation" $isRemittance={isRemittanceResponsive}>
            <Content $centered={isRemittanceResponsive}>
                <LeftSideContainer>
                    <Logo $centered={isRemittanceResponsive}>
                        <a href="/">
                            <ImgThemed src={logo} srcDark={logoDark} alt="logo" />
                        </a>
                    </Logo>
                    {isAuthenticated ? <TopMenuItems /> : null}
                </LeftSideContainer>
                {isAuthenticated ? <RightMenuItems /> : null}
                {!isAuthenticated && !isRemittance ? <SignInButton /> : null}
            </Content>
            <ViewAsClientBlock />
        </Wrapper>
    );
};
